import { useEffect, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { sessionCourante } from "../utils/session";

/**
 * LE TRADUCTEUR DE PAGE DU DASH — tout ce qui est affiché, sans toucher aux écrans.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * POURQUOI ICI, ET PAS WEGLOT. Le dash pèse 43 499 mots dans 160 fichiers, derrière un
 * identifiant. Un abonnement de traduction se facture au mot, qu'il y ait du référencement
 * à gagner ou non : l'y mettre reviendrait à louer très cher une machinerie de visibilité
 * sur des pages que personne ne peut voir. Le site public, lui, reste sur Weglot, où la
 * dépense achète quelque chose de réel.
 *
 * Celui-ci lit le texte RÉELLEMENT AFFICHÉ et le fait traduire par `/api/traduire`, donc par
 * le cache serveur : un texte déjà vu ne coûte rien, et les 436 454 caractères des deux
 * espaces privés tiennent dans le palier gratuit mensuel de DeepL.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * LES DONNÉES NE SONT PAS DU TEXTE D'INTERFACE, et c'est la difficulté propre au dash.
 *
 * Un nom d'apprenant, un intitulé de classe, un commentaire de coach ne doivent jamais
 * passer à la traduction automatique. Aucune convention de marquage n'existe aujourd'hui
 * dans ce projet : les données sont rendues à même les tableaux, sans rien qui les
 * distingue d'un libellé. Deux garde-fous, donc, et il faudra sans doute en ajouter :
 *
 *   · `data-donnees` ou la classe `.donnees` sur un élément le soustrait entièrement.
 *     C'est la porte de sortie à poser au fur et à mesure des cas constatés.
 *   · Les filtres de `estUtile()` écartent déjà les sigles en capitales, les nombres, les
 *     dates et les identifiants.
 *
 * Un nom traduit ne se voit pas tout de suite : il se découvre quand quelqu'un cherche
 * « Ouattara » et ne trouve plus rien. D'où la prudence.
 * ──────────────────────────────────────────────────────────────────────────────────────────
 *
 * QUATRE DÉFAUTS ONT ÉTÉ RENCONTRÉS SUR LA PREMIÈRE VERSION DE CE COMPOSANT, et chacun a
 * laissé sa trace ici. Ils sont documentés à l'endroit où ils sont corrigés :
 *   · une boucle de rétroaction — nos écritures se réécoutaient ;
 *   · des passages abandonnés pendant un aller-retour réseau, jamais repris ;
 *   · un lot plein qui laissait la moitié d'une page en français ;
 *   · les `placeholder` bloqués par une exclusion trop large.
 */

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

/**
 * DEUX LISTES, parce que « ne pas toucher » ne veut pas dire la même chose pour le texte et
 * pour les attributs. Ce qu'une personne TAPE dans un `<input>` lui appartient et ne doit
 * jamais être traduit — mais son `placeholder` est du texte d'interface au même titre qu'un
 * en-tête de colonne. Une seule liste interdisait les deux, et les champs restaient
 * français sur un écran anglais.
 */
const EXCLUS_TEXTE = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "KBD", "SAMP",
  "TEXTAREA",                       // son contenu EST la saisie
  "SVG", "svg", "CANVAS",
  "TITLE", "META", "LINK", "HEAD",
]);
const EXCLUS_ATTRIBUTS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "HEAD", "META", "LINK", "TITLE"]);

/* `alt` et `aria-label` comptent autant que le texte : un lecteur d'écran ne lit qu'eux. */
const ATTRIBUTS = ["placeholder", "title", "alt", "aria-label"];

const MAX_LOT = 150;

/**
 * LA MÉMOIRE DES TRADUCTIONS — gardée d'un chargement à l'autre.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * ELLE NE VIVAIT QUE LE TEMPS D'UNE VISITE. Un simple F5 la vidait, et tout le vocabulaire
 * du dash repartait au serveur — qui le resservait depuis son propre cache, donc sans
 * consommer de quota, mais au prix d'un aller-retour réseau par écran. C'est ce qui rendait
 * le PREMIER affichage de chaque onglet visiblement français.
 *
 * LE VOCABULAIRE D'INTERFACE EST FINI ET PETIT. Quelques milliers de libellés, contre les
 * 43 499 mots du dash — la différence tient aux données, qui ne passent jamais ici. Le
 * stockage local absorbe cela sans effort.
 *
 * ── UN PLAFOND, PARCE QU'UNE MÉMOIRE QUI NE SE VIDE JAMAIS FINIT PAR DÉBORDER ──
 * `localStorage` s'arrête autour de 5 Mo, et un dépassement lève au moment de l'écriture —
 * pas au moment où on l'a rempli. On borne donc à 4 000 entrées, et au-delà on repart de
 * zéro plutôt que de tenter une éviction fine : perdre le cache coûte un aller-retour,
 * le maintenir à moitié coûterait un bug difficile à voir.
 *
 * ── LA VERSION DANS LA CLÉ ──
 * Le jour où `estUtile` ou le format changent, l'ancien cache deviendrait un piège : des
 * traductions posées selon des règles qui n'existent plus. Changer le numéro suffit à le
 * mettre hors d'usage sans avoir à le purger.
 * ──────────────────────────────────────────────────────────────────────────────────────────
 */
const CLE_MEMOIRE = "bet_traductions_v1";
const MAX_MEMOIRE = 4000;

const memoire = (() => {
  try {
    const brut = localStorage.getItem(CLE_MEMOIRE);
    if (!brut) return new Map();
    const objet = JSON.parse(brut);
    /* `null` est une valeur LÉGITIME ici — « traduit, mais identique au français » — et JSON
       la conserve. On la relit telle quelle pour ne pas redemander ces phrases. */
    return new Map(Object.entries(objet));
  } catch {
    /* Stockage illisible ou refusé : on repart d'une mémoire vide. Le traducteur
       fonctionnera, simplement sans raccourci. */
    return new Map();
  }
})();

/* L'ÉCRITURE EST DIFFÉRÉE. Sérialiser quatre mille entrées à chaque phrase apprise bloquerait
   le fil principal pendant la frappe. On regroupe, et on écrit une fois le calme revenu. */
let minuteurMemoire = null;
function enregistrerMemoire() {
  clearTimeout(minuteurMemoire);
  minuteurMemoire = setTimeout(() => {
    try {
      if (memoire.size > MAX_MEMOIRE) { memoire.clear(); localStorage.removeItem(CLE_MEMOIRE); return; }
      localStorage.setItem(CLE_MEMOIRE, JSON.stringify(Object.fromEntries(memoire)));
    } catch {
      /* Quota dépassé malgré le plafond, ou navigation privée : on abandonne la persistance
         sans toucher à la mémoire vive, qui reste utilisable pour la visite en cours. */
    }
  }, 1500);
}
/** Le français d'origine, pour revenir en arrière sans recharger. */
const origines = new WeakMap();
/**
 * CE QUE NOUS AVONS NOUS-MÊMES ÉCRIT — le verrou contre la boucle.
 *
 * L'observateur écoute `characterData`, et poser une traduction MODIFIE un `nodeValue`.
 * Notre propre écriture redéclenche donc un passage, qui ramasse le texte désormais anglais
 * et le renvoie au serveur pour le traduire « du français ». Quota brûlé, observateur qui
 * ne se calme jamais. On retient donc la valeur exacte écrite, pour que la récolte l'ignore.
 */
const posees = new WeakMap();

function estUtile(s) {
  const t = (s || "").trim();
  if (t.length < 2 || t.length > 5000) return false;
  if (/^https?:\/\//i.test(t)) return false;
  if (/^[0-9\s.,:/%+€$-]+$/.test(t)) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(t)) return false;
  /* Trois minuscules consécutives : la marque d'un vrai mot. Sans cela « TOEIC » ou « BET »
     partaient au serveur pour revenir identiques — fuite constatée dans le cache. */
  if (!/\p{Ll}{3}/u.test(t)) return false;
  return true;
}

function estProtege(noeud, pourAttribut = false) {
  const liste = pourAttribut ? EXCLUS_ATTRIBUTS : EXCLUS_TEXTE;
  let e = noeud.nodeType === 3 ? noeud.parentElement : noeud;
  while (e) {
    if (liste.has(e.tagName)) return true;
    if (e.hasAttribute &&
        (e.hasAttribute("data-donnees") || e.hasAttribute("data-no-translate"))) return true;
    if (e.classList && (e.classList.contains("donnees") || e.classList.contains("no-translate"))) return true;
    if (!pourAttribut && e.isContentEditable) return true;
    e = e.parentElement;
  }
  return false;
}

const dejaPosee = (noeud, cle, valeur) => {
  const p = posees.get(noeud);
  return p ? p[cle] === valeur : false;
};

function recolter(racine) {
  const cibles = [];
  const marcheur = document.createTreeWalker(racine, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) =>
      estUtile(n.nodeValue) && !estProtege(n) && !dejaPosee(n, "#texte", n.nodeValue)
        ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  });
  let n;
  while ((n = marcheur.nextNode())) cibles.push({ noeud: n, attribut: null, texte: n.nodeValue.trim() });

  const elements = racine.nodeType === 1 ? [racine, ...racine.querySelectorAll("*")] : [];
  for (const el of elements) {
    if (estProtege(el, true)) continue;
    for (const a of ATTRIBUTS) {
      const v = el.getAttribute && el.getAttribute(a);
      if (v && estUtile(v) && !dejaPosee(el, a, v)) cibles.push({ noeud: el, attribut: a, texte: v.trim() });
    }
  }
  return cibles;
}

function poser(cible, traduit) {
  const { noeud, attribut } = cible;
  const cle = attribut || "#texte";

  if (!origines.has(noeud)) origines.set(noeud, {});
  const sauv = origines.get(noeud);
  if (sauv[cle] === undefined) sauv[cle] = attribut ? noeud.getAttribute(attribut) : noeud.nodeValue;

  const valeur = attribut ? traduit : noeud.nodeValue.replace(cible.texte, traduit);
  if (attribut) noeud.setAttribute(attribut, valeur);
  else noeud.nodeValue = valeur;

  /* Si React réécrit le français par-dessus, la valeur ne correspondra plus et le nœud
     redeviendra candidat — ce qui est exactement voulu. */
  if (!posees.has(noeud)) posees.set(noeud, {});
  posees.get(noeud)[cle] = valeur;
}

function restaurer(racine) {
  const marcheur = document.createTreeWalker(racine, NodeFilter.SHOW_TEXT, null);
  let n;
  while ((n = marcheur.nextNode())) {
    const s = origines.get(n);
    if (s && s["#texte"] !== undefined) { n.nodeValue = s["#texte"]; delete s["#texte"]; }
  }
  for (const el of racine.querySelectorAll("*")) {
    const s = origines.get(el);
    if (!s) continue;
    for (const a of ATTRIBUTS) if (s[a] !== undefined) { el.setAttribute(a, s[a]); delete s[a]; }
  }
}

/* LE JETON VIENT DE `sessionCourante()`, et non d'une lecture directe du stockage.
   Trois portails cohabitent — `coach_token`, `admin_token`, `user_token` — et une liste
   écrite à la main ici finirait par diverger de la leur. C'est exactement le défaut que
   `sessionCourante()` a été créée pour supprimer : lire ces clés à trois endroits
   différents revenait à oublier les coachs quelque part. */
const jeton = () => {
  try { return sessionCourante().access || ""; } catch { return ""; }
};

export default function TraducteurDePage() {
  const { lang } = useLanguage();
  const enCours   = useRef(false);
  const redemande = useRef(false);
  const minuteur  = useRef(null);
  /* La passe locale est planifiée sur une frame, pas sur un minuteur : elle doit se faire
     AVANT le prochain affichage, pas 200 ms après. */
  const frame     = useRef(0);

  useEffect(() => {
    if (lang !== "EN") { restaurer(document.body); return; }

    let vivant = true;
    let obs = null;

    /* Écrire sans se faire entendre : l'observateur est débranché le temps des écritures.
       Sans cela, poser quarante traductions déclencherait quarante passages, dont le
       premier avant même que le registre `posees` soit à jour. */
    const ecrireSansEcouter = (travail) => {
      if (obs) obs.disconnect();
      try { travail(); }
      finally {
        if (obs && vivant) obs.observe(document.body, { childList: true, subtree: true, characterData: true });
      }
    };

    /* ══ LA PASSE LOCALE — CE QU'ON SAIT DÉJÀ, POSÉ TOUT DE SUITE ══
       SÉPARÉE DE LA PASSE SERVEUR LE 28/10/2026. Les deux étaient confondues derrière un
       même délai de 200 ms, et ce délai se justifiait par le groupage des appels réseau.
       Mais il s'appliquait aussi à ce qui n'avait aucun besoin du réseau.

       LE SYMPTÔME : à chaque changement d'onglet, le contenu s'affichait en français puis
       basculait. Et pas la barre d'onglets — parce qu'elle, elle reste montée. Le registre
       `posees` est indexé PAR NŒUD DOM : React démonte le contenu et en recrée d'autres, donc
       des nœuds neufs, inconnus du registre, re-récoltés en entier. Un texte identique dans un
       nœud neuf est un texte neuf pour le traducteur.
       Pire : `clearTimeout` remettait le compteur à zéro à chaque mutation, et React affiche
       un onglet en plusieurs passes — l'attente réelle dépassait largement 200 ms.

       ICI, AUCUN APPEL RÉSEAU N'EST POSSIBLE : on ne pose que ce que `memoire` contient déjà.
       Elle peut donc tourner à la frame suivante, sans temporisation. Le groupage reste
       entier pour `passer()`, qui est la seule à parler au serveur. */
    function passerLocal() {
      if (!vivant || !memoire.size) return;

      const cibles = recolter(document.body);
      if (!cibles.length) return;

      ecrireSansEcouter(() => {
        for (const c of cibles) {
          const connu = memoire.get(c.texte);
          if (connu) poser(c, connu);
        }
      });
    }

    async function passer() {
      if (!vivant) return;
      /* PAS DE SESSION, PAS D'APPEL. Avant connexion, `/api/traduire` répondrait 401 à
         chaque mutation du DOM — pour rien, puisque les pages de connexion sont déjà
         traduites par les 183 clés écrites à la main de `translations.js`. Mieux vaut ne
         pas frapper à la porte que de se la faire fermer trente fois. */
      if (!jeton()) return;
      /* UN PASSAGE DEMANDÉ PENDANT UN AUTRE N'EST PAS PERDU, il est NOTÉ. C'était le défaut
         qui laissait des écrans entiers en français : pendant l'aller-retour, les données
         arrivaient du serveur, React redessinait, l'observateur rappelait — et un `return`
         sec jetait la demande. Comme plus rien ne bougeait ensuite, ce texte n'était jamais
         repris. */
      if (enCours.current) { redemande.current = true; return; }

      const cibles = recolter(document.body)
        .filter(c => !memoire.has(c.texte) || memoire.get(c.texte) !== null);

      const inconnus = [];
      ecrireSansEcouter(() => {
        for (const c of cibles) {
          const connu = memoire.get(c.texte);
          if (connu) poser(c, connu);
          else if (!memoire.has(c.texte)) inconnus.push(c);
        }
      });
      if (!inconnus.length) return;

      /* Un même libellé apparaît souvent vingt fois sur un tableau de bord. On ne l'envoie
         qu'une seule fois. */
      const uniques = [...new Set(inconnus.map(c => c.texte))].slice(0, MAX_LOT);
      if (!uniques.length) return;

      enCours.current = true;
      try {
        const r = await fetch(`${API}/api/traduire`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${jeton()}` },
          body: JSON.stringify({ textes: uniques, cible: "en" }),
        });
        if (!r.ok) return;                       // on garde le français
        const d = await r.json();
        if (!vivant || !Array.isArray(d.textes) || d.textes.length !== uniques.length) return;

        uniques.forEach((src, i) => {
          const t = d.textes[i];
          /* `null` quand la traduction n'a rien changé : on s'en souvient pour ne jamais
             redemander cette phrase. */
          memoire.set(src, typeof t === "string" && t !== src ? t : null);
        });
        enregistrerMemoire();
        ecrireSansEcouter(() => {
          for (const c of inconnus) {
            const t = memoire.get(c.texte);
            if (t) poser(c, t);
          }
        });
      } catch {
        /* Réseau coupé : l'écran reste en français, donc lisible. */
      } finally {
        enCours.current = false;
      }

      /* DEUX RAISONS DE REPASSER, et il faut les deux. `redemande` : le DOM a changé pendant
         l'attente. `uniques.length === MAX_LOT` : le lot était plein, il reste donc
         probablement des phrases — sans cette reprise, un écran riche s'arrêtait à la
         moitié et rien ne venait finir le travail. */
      if (vivant && (redemande.current || uniques.length === MAX_LOT)) {
        redemande.current = false;
        setTimeout(passer, 0);
      }
    }

    /* LE CONNU D'ABORD, ET SANS ATTENDRE. Au montage — après un rechargement, ou au clic sur
       EN — la mémoire persistée contient déjà presque tout le vocabulaire de l'écran. L'appliquer
       avant de parler au serveur fait que la page s'affiche directement en anglais ; sans cet
       appel, elle restait française le temps de l'aller-retour, alors que la réponse était
       déjà dans le navigateur. */
    passerLocal();
    passer();

    /* L'observateur rattrape ce que React redessine et ce qui arrive du serveur. Il est
       TEMPORISÉ : sans cela, un tableau de cinquante lignes déclencherait cinquante
       passages. */
    obs = new MutationObserver(() => {
      /* DEUX RYTHMES POUR DEUX TRAVAUX.
         `requestAnimationFrame` pour le connu : la pose se fait avant le prochain affichage,
         donc le visiteur ne voit jamais le français. Il n'est PAS temporisé — le temporiser
         reviendrait à recréer le défaut qu'on corrige.
         `setTimeout(200)` pour l'inconnu : le groupage des appels reste indispensable, sans
         lui chaque ligne d'un tableau déclencherait une requête et le quota partirait en
         quelques jours. */
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(passerLocal);

      clearTimeout(minuteur.current);
      minuteur.current = setTimeout(passer, 200);
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });

    return () => {
      vivant = false;
      obs.disconnect();
      clearTimeout(minuteur.current);
      cancelAnimationFrame(frame.current);
    };
  }, [lang]);

  return null;
}
